import dayjs from 'dayjs';
import { isObject, isUndef } from '@aviata/type-guards';

import { dateUtils } from '../../shared/lib';
import { citySearchHistory } from '../../features';
import { searchCities } from '../../entities';
import type { RouteState, Passengers, CabinClass, LastSearch } from './types';

const isRoute = (route: unknown): route is RouteState =>
  isObject(route) &&
  Array.isArray(route.dates) &&
  citySearchHistory.lib.isMappedCity(route.arr) &&
  citySearchHistory.lib.isMappedCity(route.dep);

const isRoutesArray = (routes: unknown): routes is RouteState =>
  Array.isArray(routes) && routes.every(isRoute);

const isPassengersState = (passengers: unknown): passengers is Passengers =>
  isObject(passengers) &&
  'adt' in passengers &&
  'chd' in passengers &&
  'inf' in passengers &&
  'yth' in passengers;

const isCabinClass = (cabinClass: string): cabinClass is CabinClass =>
  ['E', 'B'].includes(cabinClass);

const isLastSearch = (v: unknown): v is LastSearch =>
  isObject(v) &&
  isRoutesArray(v.routes) &&
  isPassengersState(v.passengers) &&
  isCabinClass(v.cabinClass) &&
  !isUndef(v.isYouth);

const getValidDate = (dateStr: string) => {
  if (dateStr) {
    const date = new Date(dateStr);
    return dayjs().isAfter(date) && !dayjs().isSame(date, 'day')
      ? dateUtils.getTomorrowDate()
      : dateStr;
  }

  return dateUtils.getTomorrowDate();
};

const getValidatedRoute = (route: RouteState) => {
  const [departureDate, returnDate] = route.dates || [];

  return {
    ...route,
    dates: [
      getValidDate(departureDate),
      getValidDate(departureDate) !== departureDate ? '' : returnDate,
    ].filter(date => Boolean(date)),
  };
};

const enrichPassengersWithDefaultData = (
  passengers: Passengers,
  isYouth: boolean,
) => {
  return isYouth
    ? {
        ...passengers,
        adt: 1,
      }
    : {
        ...passengers,
        yth: 1,
      };
};

const localizeRoutes = (route: RouteState) => {
  return {
    ...route,
    dep: searchCities.lib.localizeLocation(route.dep),
    arr: searchCities.lib.localizeLocation(route.arr),
  };
};

/**
 * Last search history in local storage may be invalid.
 * This method returns validated last search history data
 */
const getValidatedLastSearch = (lastSearch: LastSearch) => {
  const validatedRoutes = lastSearch.routes
    .map(getValidatedRoute)
    .map(localizeRoutes);

  const validatedPassengers = enrichPassengersWithDefaultData(
    lastSearch.passengers,
    lastSearch.isYouth,
  );

  return {
    ...lastSearch,
    routes: validatedRoutes,
    passengers: validatedPassengers,
  };
};

export const lastSearchLib = {
  isLastSearch,
  getValidatedLastSearch,
};
